/**
 * useSearchAutocomplete Hook
 *
 * Fetches product + store suggestions for the search input dropdown.
 * Shared by SearchOverlay and SearchView.
 */

import type { GroupedHits, GroupedStores } from '@/components/search/SearchDropdown';
import { fetchBatchSearch } from '@/lib/search/fetchBatchSearch';
import { devError } from '@/lib/utils/devLog';
import { useCallback, useRef, useState } from 'react';

export function useSearchAutocomplete(lang: string) {
  const [groupedHits, setGroupedHits] = useState<GroupedHits[]>([]);
  const [groupedStores, setGroupedStores] = useState<GroupedStores | undefined>(undefined);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const justClearedRef = useRef(false);
  const requestIdRef = useRef(0);

  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        requestIdRef.current += 1;
        setGroupedHits([]);
        setGroupedStores(undefined);
        setShowDropdown(false);
        setIsSearching(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setIsSearching(true);
      setShowDropdown(true);

      try {
        const { groupedHits: hits, groupedStores: stores } = await fetchBatchSearch(
          lang,
          searchQuery
        );

        if (requestId !== requestIdRef.current) {
          return;
        }

        setGroupedHits(hits);
        setGroupedStores(stores);
        setShowDropdown(true);
      } catch (error) {
        if (requestId === requestIdRef.current) {
          devError('[useSearchAutocomplete] Search error:', error);
          setGroupedHits([]);
          setGroupedStores(undefined);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsSearching(false);
        }
      }
    },
    [lang]
  );

  const handleClear = useCallback(() => {
    justClearedRef.current = true;
    requestIdRef.current += 1;
    setGroupedHits([]);
    setGroupedStores(undefined);
    setShowDropdown(false);
    setTimeout(() => {
      justClearedRef.current = false;
    }, 0);
  }, []);

  const handleFocus = useCallback(
    (query: string) => {
      if (justClearedRef.current) {
        return;
      }

      const hasResults =
        groupedHits.some((g) => g.hits.length > 0) ||
        (groupedStores && groupedStores.hits.length > 0);

      if (query.trim() && hasResults) {
        setShowDropdown(true);
      }
    },
    [groupedHits, groupedStores]
  );

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
  }, []);

  const resetAutocomplete = useCallback(() => {
    requestIdRef.current += 1;
    setGroupedHits([]);
    setGroupedStores(undefined);
    setShowDropdown(false);
    setIsSearching(false);
  }, []);

  return {
    groupedHits,
    groupedStores,
    isSearching,
    showDropdown,
    performSearch,
    handleClear,
    handleFocus,
    closeDropdown,
    resetAutocomplete,
  };
}
